import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Carrera from './componenters/carrera';
import CarrerasAnteriores from './componenters/carreras_anteriores';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/Login.context";
import Timeline from '@mui/icons-material/Timeline';
import SsidChart from '@mui/icons-material/SsidChart';
import SmartToy from '@mui/icons-material/SmartToy';
import RaceSelector from './componenters/RaceSelector';
import HandicapView from './handicap';
import PredicHandicapView from './componenters/predic_handicap';
import AgentView from '../agent/AgentView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  id?: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`carrera-tabpanel-${index}`}
      aria-labelledby={`carrera-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SelectCarreras: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const auth = useAuth();
  const { id, hipodromoId } = useParams<{ hipodromoId: string, id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isPremium = auth.user?.subscription?.name === 'Freemium' || 
                    auth.user?.subscription?.name === 'Freemium IA' || 
                    auth.user?.subscription?.name === 'Premium' || 
                    auth.user?.subscription?.name === 'Premium IA' ||
                    auth.user?.subscription?.name === 'Básico';

  const isAgentEnabled = auth.user?.subscription?.name === 'Freemium IA' || 
                         auth.user?.subscription?.name === 'Premium IA';

  // Mapeo dinámico de índices a nombres según permisos
  const getTabName = (index: number) => {
    let currentTabs = ['actual', 'anterior'];
    if (isPremium) currentTabs.push('handicap', 'estadisticas');
    if (isAgentEnabled) currentTabs.push('agente');
    return currentTabs[index] || 'actual';
  };

  const getTabIndex = (name: string) => {
    let currentTabs = ['actual', 'anterior'];
    if (isPremium) currentTabs.push('handicap', 'estadisticas');
    if (isAgentEnabled) currentTabs.push('agente');
    const idx = currentTabs.indexOf(name);
    return idx >= 0 ? idx : 0;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');
    if (tabParam) {
      setSelectedTab(getTabIndex(tabParam));
    } else {
      setSelectedTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search, isPremium, isAgentEnabled]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const tabName = getTabName(newValue);
    navigate(`/hipodromo/${hipodromoId}/carrera/${id}?tab=${tabName}`);
  };

  // Definición de pestañas dinámica
  const tabs: Array<{ label: string; icon?: React.ReactElement; color: string }> = [
    { label: 'Actual', icon: undefined, color: 'inherit' },
    { label: 'Anterior', icon: undefined, color: 'inherit' },
  ];

  if (id && isPremium) {
    tabs.push({ label: 'Handicap', icon: <Timeline />, color: 'primary.main' });
    tabs.push({ label: 'Estadísticas', icon: <SsidChart />, color: 'secondary.main' });
  }

  if (id && isAgentEnabled) {
    tabs.push({ label: 'Agente', icon: <SmartToy />, color: 'info.main' });
  }

  const renderTabs = (orientation: 'horizontal' | 'vertical') => (
    <Tabs
      id="race-view-tabs"
      orientation={orientation}
      variant={orientation === 'vertical' ? "scrollable" : "scrollable"}
      scrollButtons="auto"
      value={selectedTab}
      onChange={handleChange}
      aria-label="carreras tabs"
      centered={orientation === 'horizontal'}
      sx={{ 
        borderRight: orientation === 'vertical' ? 1 : 0, 
        borderBottom: orientation === 'horizontal' ? 1 : 0, 
        borderColor: 'divider',
        minWidth: orientation === 'vertical' ? '140px' : 'auto'
      }}
    >
      {tabs.map((tab, index) => (
        <Tab 
          key={index} 
          label={tab.label} 
          {...(tab.icon ? { icon: tab.icon, iconPosition: "start" } : {})}
          sx={{ 
            '&.Mui-selected': { color: tab.color },
            color: tab.color !== 'inherit' ? tab.color : undefined,
            minHeight: '48px',
            textTransform: 'none',
            fontSize: '0.9rem'
          }} 
        />
      ))}
    </Tabs>
  );

  const DesktopView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'row', height: 'calc(100vh - 64px - 60px)' }}>
      <Box sx={{ width: '20%', minWidth: '200px', borderRight: 1, borderColor: 'divider', p: 0 }}>
        {renderTabs('vertical')}
      </Box>
      <Box sx={{ width: '80%', p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {id && (
            <>
              <CustomTabPanel value={selectedTab} index={0} id="race-content-panel">
                <Carrera id={Number(id)} />
              </CustomTabPanel>
              <CustomTabPanel value={selectedTab} index={1} id="race-tab-previous">
                <CarrerasAnteriores id={Number(id)} />
              </CustomTabPanel>
              {isPremium && (
                <>
                  <CustomTabPanel value={selectedTab} index={2} id="race-tab-handicap">
                    <HandicapView raceId={Number(id)} />
                  </CustomTabPanel>
                  <CustomTabPanel value={selectedTab} index={3} id="race-tab-predictions">
                    <PredicHandicapView raceId={Number(id)} />
                  </CustomTabPanel>
                </>
              )}
              {isAgentEnabled && (
                <CustomTabPanel value={selectedTab} index={isPremium ? 4 : 2} id="race-tab-agent">
                  <AgentView />
                </CustomTabPanel>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  const MobileView = () => (
    <Box sx={{ width: '100%', paddingBottom: '20px' }}>
      <Box sx={{ bgcolor: 'background.paper', position: 'sticky', top: '0px', zIndex: 900 }}>
        {renderTabs('horizontal')}
      </Box>

      {id && (
        <>
          <CustomTabPanel value={selectedTab} index={0} id="race-content-panel">
            <Carrera id={Number(id)} />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={1} id="race-tab-previous">
            <CarrerasAnteriores id={Number(id)} />
          </CustomTabPanel>
          {isPremium && (
            <>
              <CustomTabPanel value={selectedTab} index={2} id="race-tab-handicap">
                <HandicapView raceId={Number(id)} />
              </CustomTabPanel>
              <CustomTabPanel value={selectedTab} index={3} id="race-tab-predictions">
                <PredicHandicapView raceId={Number(id)} />
              </CustomTabPanel>
            </>
          )}
          {isAgentEnabled && (
            <CustomTabPanel value={selectedTab} index={isPremium ? 4 : 2} id="race-tab-agent">
              <AgentView />
            </CustomTabPanel>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', marginTop: { xs: '15px', md: '5px' } }} id="race-selector-container">
      <RaceSelector />
      {isMobile ? <MobileView /> : <DesktopView />}
    </Box>
  );
};

export default SelectCarreras;